import { Injectable } from '@angular/core';
import * as HttpStatus from 'http-status-codes';
import { List } from 'immutable';
import { extend, isEqual } from 'lodash';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { BehaviorSubject } from 'rxjs';
import { AuthDataDispatcher } from '../auth-data/auth-data.dispatcher';
import { AuthDataService } from '../auth-data/auth-data.service';
import { AuthDataStore } from '../auth-data/auth-data.store';
import { environment } from '../../../environments/environment';

@Injectable()
export class AttachmentsService {
  private _uploader: FileUploader;
  private URL = `${environment.gatewayUrl}/files`;
  private _baseSettings = {
    url: this.URL,
    autoUpload: true,
  };

  private _refreshAccessTokenTriggers: List<FileItem> = List<FileItem>();

  private _onAfterAddingFile: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private _onProgressItem: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private _onSuccessItem: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private _onErrorItem: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  onAfterAddingFile$ = this._onAfterAddingFile.asObservable();
  onProgressItem$ = this._onProgressItem.asObservable();
  onSuccessItem$ = this._onSuccessItem.asObservable();
  onErrorItem$ = this._onErrorItem.asObservable();

  constructor(
    private _authDataDispatcher: AuthDataDispatcher,
    private _authDataStore: AuthDataStore,
    private _authDataService: AuthDataService
  ) {
    this._authDataStore.getAccessToken().subscribe((accessToken: string) => {
      this._uploader = new FileUploader(this._getSettings(accessToken));

      this._uploader.onAfterAddingFile = (file) => {
        file.withCredentials = false; // TODO: no credentials for dev environment (cors)
        this._onAfterAddingFile.next(file);
      };
      this._uploader.onProgressItem = (file, progress) =>
        this._onProgressItem.next([file, progress]);
      this._uploader.onSuccessItem = (file, response) =>
        this._onSuccessItem.next([file, response]);
      this._uploader.onErrorItem = (file, response, status) => {
        const error = JSON.parse(response || null);

        if (status === HttpStatus.UNAUTHORIZED) {
          if (!this._hasAccessTokenAlreadyBeenRefreshed(file)) {
            this._refreshAccessTokenTriggers = this._refreshAccessTokenTriggers.push(
              file
            );

            this._authDataService
              .refreshAccessToken()
              .subscribe((refreshAccessToken: string) => {
                if (refreshAccessToken) {
                  this._uploader.setOptions(
                    this._getSettings(refreshAccessToken)
                  );
                  this._uploader.uploadItem(file);
                } else {
                  this._logout();
                }
              });
          } else {
            this._logout();
          }
        } else {
          this._onErrorItem.next([file, error]);
        }
      };
    });
  }

  addToQueue(files: any[]) {
    this._authDataStore.getAccessToken().subscribe((accessToken: string) => {
      this._uploader.setOptions(this._getSettings(accessToken));

      this._uploader.addToQueue(files);
    });
  }

  cancelAll() {
    this._uploader.cancelAll();
    this._uploader.clearQueue();
  }

  cancelUpload(file: FileItem) {
    this._uploader.cancelItem(file);
    this._uploader.removeFromQueue(file);
  }

  uploadFile(file: FileItem) {
    this._uploader.uploadItem(file);
  }

  private _getSettings(accessToken: string): any {
    return extend({}, this._baseSettings, {
      authToken: 'Bearer ' + accessToken,
    });
  }

  private _hasAccessTokenAlreadyBeenRefreshed(fileItem: FileItem): boolean {
    return (
      this._refreshAccessTokenTriggers.findIndex((triggers: FileItem) =>
        isEqual(triggers, fileItem)
      ) > -1
    );
  }

  private _logout() {
    this._authDataDispatcher.logout(true);
  }
}
